import { supabase } from './supabase';

export type AppNotification = {
  id: string;
  text: string;
  type: 'chat' | 'provider' | 'booking' | 'proposal';
  /** Where tapping the notification leads. */
  href?: string;
};

const cleanInlineText = (value: unknown, fallback: string): string => {
  if (typeof value !== 'string') {
    return fallback;
  }

  const normalized = value
    .normalize('NFKC')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/[\u0000-\u001F\u007F]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return normalized || fallback;
};

const normalizeCity = (value: unknown) => {
  if (typeof value !== 'string') return '';
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/gi, ' ')
    .trim()
    .toLowerCase();
};

const getCityTerms = (value: unknown) => {
  const normalized = normalizeCity(value);
  if (!normalized) return [];
  return Array.from(new Set([normalized, ...normalized.split(' ')])).filter(
    (term) => term.length >= 3
  );
};

export const isSameCityDemand = (booking: { address?: string | null }, providerCity: string) => {
  const cityTerms = getCityTerms(providerCity);
  if (cityTerms.length === 0) return true;
  const address = normalizeCity(booking.address);
  return cityTerms.some((term) => address.includes(term));
};

export const loadClientNotifications = async (userId: string): Promise<AppNotification[]> => {
  const builtNotifications: AppNotification[] = [];

  const { data: chatRooms } = await supabase
    .from('chat_rooms')
    .select('id, provider:profiles!chat_rooms_provider_id_fkey(full_name)')
    .eq('client_id', userId);

  const roomRows = chatRooms ?? [];
  const roomIds = roomRows.map((room: any) => room.id);
  const roomProviderNameById = new Map<string, string>();

  roomRows.forEach((room: any) => {
    roomProviderNameById.set(room.id, cleanInlineText(room.provider?.full_name, 'Un prestataire'));
  });

  if (roomIds.length > 0) {
    const { data: recentMessages } = await supabase
      .from('chat_messages')
      .select('id, chat_room_id, sender_id, message, created_at')
      .in('chat_room_id', roomIds)
      .neq('sender_id', userId)
      .not('message', 'is', null)
      .order('created_at', { ascending: false })
      .limit(30);

    const seenRoomIds = new Set<string>();
    (recentMessages ?? []).forEach((message: any) => {
      const text = typeof message.message === 'string' ? message.message.trim() : '';
      if (!text || seenRoomIds.has(message.chat_room_id)) {
        return;
      }

      seenRoomIds.add(message.chat_room_id);
      const providerName = cleanInlineText(
        roomProviderNameById.get(message.chat_room_id),
        'Un prestataire'
      );
      builtNotifications.push({
        id: `chat-${message.id}`,
        text: cleanInlineText(
          `${providerName} vous a envoyé un message.`,
          'Vous avez reçu un message.'
        ),
        type: 'chat',
        href: `/app/messages/${message.chat_room_id}`,
      });
    });
  }

  const { data: clientDemands } = await supabase
    .from('bookings')
    .select('id, service_id, status, services(name)')
    .eq('client_id', userId)
    .not('status', 'in', '(completed,cancelled,rejected)');

  const activeDemands = clientDemands ?? [];

  const pendingDemands = activeDemands.filter((demand: any) => demand.status === 'pending');
  if (pendingDemands.length > 0) {
    const serviceNameByBookingId = new Map<string, string>(
      pendingDemands.map((demand: any) => [demand.id, demand.services?.name])
    );
    const { data: proposals } = await supabase
      .from('booking_proposals')
      .select('id, booking_id, price, profiles!booking_proposals_provider_id_fkey(full_name)')
      .in('booking_id', [...serviceNameByBookingId.keys()])
      .eq('status', 'pending')
      .order('created_at', { ascending: false })
      .limit(20);

    (proposals ?? []).forEach((proposal: any) => {
      const providerName = cleanInlineText(proposal.profiles?.full_name, 'Un prestataire');
      const serviceName = cleanInlineText(
        serviceNameByBookingId.get(proposal.booking_id),
        'service'
      );
      builtNotifications.push({
        id: `proposal-${proposal.id}`,
        text: cleanInlineText(
          `${providerName} propose ${proposal.price} MAD pour votre demande de ${serviceName}.`,
          'Vous avez reçu une nouvelle proposition.'
        ),
        type: 'proposal',
        href: `/app/bookings/${proposal.booking_id}#propositions`,
      });
    });
  }

  const serviceIds = [...new Set(activeDemands.map((d: any) => d.service_id).filter(Boolean))];

  if (serviceIds.length > 0) {
    const { data: serviceProviders } = await supabase
      .from('provider_services')
      .select('provider_id, service_id')
      .in('service_id', serviceIds);

    const providerServiceRows = serviceProviders ?? [];
    const providerIds = [
      ...new Set(providerServiceRows.map((row: any) => row.provider_id).filter(Boolean)),
    ];

    if (providerIds.length > 0) {
      const twoWeeksAgoIso = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString();
      const { data: approvedProviders } = await supabase
        .from('providers')
        .select('id, updated_at, profiles(full_name)')
        .in('id', providerIds)
        .eq('is_approved', true)
        .gte('updated_at', twoWeeksAgoIso);

      const approvedProviderMap = new Map<string, string>();
      (approvedProviders ?? []).forEach((provider: any) => {
        approvedProviderMap.set(provider.id, provider.profiles?.full_name ?? 'un prestataire');
      });

      const notifiedServices = new Set<string>();
      activeDemands.forEach((demand: any) => {
        if (!demand.service_id || notifiedServices.has(demand.service_id)) {
          return;
        }

        const match = providerServiceRows.find(
          (row: any) =>
            row.service_id === demand.service_id && approvedProviderMap.has(row.provider_id)
        );

        if (!match) {
          return;
        }

        notifiedServices.add(demand.service_id);
        const providerName = cleanInlineText(
          approvedProviderMap.get(match.provider_id),
          'un prestataire'
        );
        const serviceName = cleanInlineText(demand.services?.name, 'service');

        builtNotifications.push({
          id: `provider-${demand.id}-${match.provider_id}`,
          text: cleanInlineText(
            `Découvrez ${providerName}, disponible pour votre demande de ${serviceName}.`,
            'Un prestataire peut répondre à votre demande.'
          ),
          type: 'provider',
          href: `/app/providers/${match.provider_id}`,
        });
      });
    }
  }

  return builtNotifications;
};

export const loadProviderNotifications = async (userId: string): Promise<AppNotification[]> => {
  const builtNotifications: AppNotification[] = [];

  const { data: providerProfile } = await supabase
    .from('profiles')
    .select('city')
    .eq('id', userId)
    .maybeSingle();

  const { data: providerApplication } = await supabase
    .from('provider_applications')
    .select('city')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  const providerCity = providerProfile?.city || providerApplication?.city || '';

  const { data: chatRooms } = await supabase
    .from('chat_rooms')
    .select('id, client:profiles!chat_rooms_client_id_fkey(full_name)')
    .eq('provider_id', userId);

  const roomRows = chatRooms ?? [];
  const roomIds = roomRows.map((room: any) => room.id);
  const roomClientNameById = new Map<string, string>();

  roomRows.forEach((room: any) => {
    roomClientNameById.set(room.id, cleanInlineText(room.client?.full_name, 'Un client'));
  });

  if (roomIds.length > 0) {
    const { data: recentMessages } = await supabase
      .from('chat_messages')
      .select('id, chat_room_id, sender_id, message, created_at')
      .in('chat_room_id', roomIds)
      .neq('sender_id', userId)
      .not('message', 'is', null)
      .order('created_at', { ascending: false })
      .limit(30);

    const seenRoomIds = new Set<string>();
    (recentMessages ?? []).forEach((message: any) => {
      const text = typeof message.message === 'string' ? message.message.trim() : '';
      if (!text || seenRoomIds.has(message.chat_room_id)) {
        return;
      }

      seenRoomIds.add(message.chat_room_id);
      const clientName = cleanInlineText(roomClientNameById.get(message.chat_room_id), 'Un client');
      builtNotifications.push({
        id: `chat-${message.id}`,
        text: cleanInlineText(
          `${clientName} vous a envoyé un message.`,
          'Vous avez reçu un message.'
        ),
        type: 'chat',
        href: `/pro/messages/${message.chat_room_id}`,
      });
    });
  }

  const { data: myServicesRows } = await supabase
    .from('provider_services')
    .select('service_id')
    .eq('provider_id', userId);

  const serviceIds = [
    ...new Set((myServicesRows ?? []).map((row: any) => row.service_id).filter(Boolean)),
  ];

  if (serviceIds.length > 0) {
    const { data: pendingBookings } = await supabase
      .from('bookings')
      .select('id, service_id, address, services(name)')
      .eq('status', 'pending')
      .in('service_id', serviceIds)
      .order('created_at', { ascending: false })
      .limit(20);

    const pendingRows = (pendingBookings ?? []).filter((booking: any) =>
      isSameCityDemand(booking, providerCity)
    );
    const pendingIds = pendingRows.map((booking: any) => booking.id);

    let proposedBookingIds = new Set<string>();
    if (pendingIds.length > 0) {
      const { data: proposals } = await supabase
        .from('booking_proposals')
        .select('booking_id')
        .eq('provider_id', userId)
        .in('booking_id', pendingIds);

      proposedBookingIds = new Set((proposals ?? []).map((proposal: any) => proposal.booking_id));
    }

    pendingRows
      .filter((booking: any) => !proposedBookingIds.has(booking.id))
      .slice(0, 3)
      .forEach((booking: any) => {
        const serviceName = cleanInlineText(booking.services?.name, 'service');
        builtNotifications.push({
          id: `demand-${booking.id}`,
          text: cleanInlineText(
            `Nouvelle demande de ${serviceName} disponible. Envoyez votre proposition.`,
            'Une nouvelle demande est disponible. Envoyez votre proposition.'
          ),
          type: 'booking',
          href: `/pro/requests/${booking.id}/apply`,
        });
      });
  }

  const { data: acceptedAssignedBookings } = await supabase
    .from('bookings')
    .select('id, services(name), status, chat_rooms(id)')
    .eq('provider_id', userId)
    .eq('status', 'accepted')
    .order('updated_at', { ascending: false })
    .limit(3);

  (acceptedAssignedBookings ?? []).forEach((booking: any) => {
    const serviceName = cleanInlineText(booking.services?.name, 'service');
    // chat_rooms.booking_id is unique, but PostgREST may still return an array.
    const room = Array.isArray(booking.chat_rooms) ? booking.chat_rooms[0] : booking.chat_rooms;
    builtNotifications.push({
      id: `accepted-${booking.id}`,
      text: cleanInlineText(
        `Un client a accepté votre proposition de ${serviceName}. Vous pouvez démarrer la mission.`,
        'Un client a accepté votre proposition. Vous pouvez démarrer la mission.'
      ),
      type: 'provider',
      href: room?.id ? `/pro/messages/${room.id}` : '/pro/requests?tab=missions',
    });
  });

  return builtNotifications;
};
